import React, { useMemo, useState } from "react";
import { Box, Grid, Typography, Tooltip, ToggleButton, ToggleButtonGroup } from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { AdjustedProductType, InvestmentPathDetail, Product } from "../../../interfaces/DTO/mislaka_xml_models";
import { MimshakData } from "../../../interfaces/DTO/mimshakData";
import { MimshakDataUtil } from "../../../services/mimshakDataUtil";
import { formatPercentage, formatCurrency } from "../../../utils/namesFormater";
import { FundSumDropMap } from "../../../interfaces/DTO/simulation";
import { ChangesDataUtils } from "../../FeesLab/Reports/ChangesDataUtils";
import { YIELD_FIELDS, computeWeightedYieldsFromPaths, buildExcludedTooltip } from "../../../services/yieldCalculator";
import { checkAndFixInvestmentPathAmounts, buildIntegrityWarningText } from "../../../services/simulationDataIntegrityCheck";

const NO_PATH_PENSION_TYPES = new Set<AdjustedProductType>([
  AdjustedProductType.BASIC_PENSION_FUND,
  AdjustedProductType.OLD_COMPREHENSIVE_PENSION,
  AdjustedProductType.OLD_BASIC_PENSION,
]);

interface Props {
  title: string;
  data?: MimshakData[];
  originalData?: MimshakData[];
  products?: Product[];
  fundSumDropMap?: FundSumDropMap;
  showTotalAmount?: boolean;
  forceShowNewFunds?: boolean;
  hideCalculationInfo?: boolean;
  textColor?: string;
}

const WeightedProductYieldsSummary: React.FC<Props> = ({ title, data, originalData, products: propsProducts, fundSumDropMap, showTotalAmount, forceShowNewFunds, hideCalculationInfo, textColor }) => {
  const [showNewFundsState, setShowNewFunds] = useState(true);
  const showNewFunds = forceShowNewFunds !== undefined ? forceShowNewFunds : showNewFundsState;

  const hasNewFunds = useMemo(() => {
    const hasFundSumAdditions = !!fundSumDropMap && Object.values(fundSumDropMap).some(
      additions => additions && Object.values(additions).some(v => v && v > 0)
    );
    if (hasFundSumAdditions) return true;
    if (originalData && data) {
      const originalPolicies = new Set(originalData.map(r => r.policy_number));
      return data.some(r => !originalPolicies.has(r.policy_number));
    }
    return false;
  }, [fundSumDropMap, originalData, data]);

  const effectiveData = useMemo(() => {
    let result = data;
    if (hasNewFunds && !showNewFunds && data && originalData) {
      const originalPolicies = new Set(originalData.map(r => r.policy_number));
      result = data.filter(r => originalPolicies.has(r.policy_number));
    }
    if (!result) return result;
    // Exclude pension types that don't report investment_paths reliably and have no paths
    return result.filter(r => {
      if (r.adjusted_product_type == null || !NO_PATH_PENSION_TYPES.has(r.adjusted_product_type as AdjustedProductType)) return true;
      return (r.data?.products ?? []).flatMap(p => p.policies_or_accounts ?? []).some(p => (p.investment_paths ?? []).length > 0);
    });
  }, [hasNewFunds, showNewFunds, data, originalData]);

  const effectiveFundSumDropMap = hasNewFunds && !showNewFunds ? undefined : fundSumDropMap;

  const isModified = !!originalData;

  const integrityCheck = useMemo(() => {
    if (!effectiveData || !isModified) return null;
    return checkAndFixInvestmentPathAmounts(effectiveData);
  }, [effectiveData, isModified]);

  const checkedData = integrityCheck?.correctedData ?? effectiveData;

  const products = useMemo(() => {
    if (checkedData) return MimshakDataUtil.extractAllProducts(checkedData);
    return propsProducts || [];
  }, [checkedData, propsProducts]);

  const yieldResult = useMemo(() => {
    const allPaths: { path: InvestmentPathDetail; policyNumber?: string }[] = [];

    if (checkedData) {
      for (const row of checkedData) {
        const rowPolicies = row.data?.products?.flatMap((p) => p.policies_or_accounts) || [];
        const additions = effectiveFundSumDropMap?.[row.policy_number];
        const extra = ChangesDataUtils.getTotalFundAddition(additions);
        const allRowPaths: InvestmentPathDetail[] = rowPolicies.flatMap((p) => p.investment_paths || []);
        const extraPerPath = allRowPaths.length > 0 ? extra / allRowPaths.length : 0;

        for (const policy of rowPolicies) {
          for (const path of policy.investment_paths || []) {
            const adjustedPath = { ...path };
            if (adjustedPath.accumulated_amount !== undefined) {
              adjustedPath.accumulated_amount = adjustedPath.accumulated_amount + extraPerPath;
            }
            allPaths.push({ path: adjustedPath, policyNumber: row.policy_number });
          }
        }
      }
    } else {
      for (const product of products) {
        for (const policy of product.policies_or_accounts || []) {
          for (const path of policy.investment_paths || []) {
            allPaths.push({ path: { ...path }, policyNumber: policy.policy_number });
          }
        }
      }
    }

    return computeWeightedYieldsFromPaths(allPaths);
  }, [checkedData, effectiveFundSumDropMap, products]);

  const calculationBaseAmount = yieldResult.totalAmount;

  const totalAmount = useMemo(() => {
    if (!showTotalAmount || !checkedData) return null;
    return checkedData.reduce((sum, row) => {
      const rowPolicies = row.data?.products?.flatMap((p) => p.policies_or_accounts) || [];
      for (const policy of rowPolicies) {
        const policyTotal = (policy.investment_paths || []).reduce((s, p) => s + (p.accumulated_amount ?? 0), 0);
        sum += policyTotal;
      }
      return sum;
    }, 0);
  }, [showTotalAmount, checkedData]);

// Only show the 4 fields this component displays (no 6M)
  const displayFields = YIELD_FIELDS.filter(f => f.key !== 'cumulative_6m_yield');

  return (
    <Box sx={{ p: 1, direction: "rtl" }}>
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
        <Typography variant="body1" sx={{ mb: 2, fontWeight: 700, color: textColor ?? "text.secondary" }}>{title}</Typography>
        {totalAmount != null && totalAmount > 0 && (
          <Typography variant="body1" sx={{ ml: "auto", mb: 2, fontWeight: 700, color: textColor ?? "text.secondary" }}>{formatCurrency(totalAmount)}</Typography>
        )}
      </Box>
      <Grid container spacing={2}>
        {displayFields.map(({ key, label }) => {
          const { average, excluded } = yieldResult[key];
          return (
            <Grid item xs={6} sm={3} md={3} key={key}>
              <Box textAlign="center" borderRight={1} borderColor="divider" pr={2}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5 }}>
                  <Typography variant="subtitle2" gutterBottom sx={{ mb: 0, ...(textColor && { color: textColor }) }}>
                    {label}
                  </Typography>
                  {excluded.length > 0 && (
                    <Tooltip
                      title={
                        <span style={{ whiteSpace: "pre-line", direction: "rtl", display: "block", textAlign: "right" }}>
                          {buildExcludedTooltip(excluded)}
                        </span>
                      }
                      arrow
                    >
                      <WarningAmberIcon color="warning" sx={{ fontSize: "1rem", verticalAlign: "middle" }} />
                    </Tooltip>
                  )}
                </Box>
                <Typography variant="h6" sx={{ direction: "ltr", ...(textColor && { color: textColor }) }}>
                  {formatPercentage(average)}
                </Typography>
              </Box>
            </Grid>
          );
        })}
      </Grid>


      {integrityCheck && !integrityCheck.valid && (
        <Tooltip
          arrow
          title={
            <span style={{ whiteSpace: "pre-line", direction: "rtl", display: "block", textAlign: "right" }}>
              {buildIntegrityWarningText(integrityCheck.issues)}
            </span>
          }
        >
          <Typography variant="caption" sx={{ display: "flex", alignItems: "center", gap: 0.5, color: "error.main", cursor: "default", mt: 1 }}>
            <WarningAmberIcon sx={{ fontSize: "1rem", color: "error.main" }} />
            * נמצאו אי-התאמות בנתוני המסלולים — הסכומים תוקנו אוטומטית
          </Typography>
        </Tooltip>
      )}
      <Box mt={1} display="flex" justifyContent="flex-start" alignItems="center" gap={2}>
        {hasNewFunds && forceShowNewFunds === undefined && (
          <ToggleButtonGroup
            size="small"
            value={showNewFunds ? "new" : "original"}
            exclusive
            onChange={(_, val) => { if (val !== null) setShowNewFunds(val === "new"); }}
          >
            <ToggleButton value="original">כספים מקוריים</ToggleButton>
            <ToggleButton value="new">כולל כספים חדשים</ToggleButton>
          </ToggleButtonGroup>
        )}
        {hasNewFunds && forceShowNewFunds === undefined && calculationBaseAmount != null && (
          <Typography variant="caption" color="text.secondary">
            {formatCurrency(calculationBaseAmount)}
          </Typography>
        )}
        {!hideCalculationInfo && <Tooltip
          title="החישוב מציג ממוצע משוקלל של התשואות כפי שדווחו לגמל נט, בהתאם לסכומים הנוכחיים במסלולי ההשקעה. מסלולי IRA (בניהול אישי) ומסלולים ללא נתונים מוחרגים מהחישוב."
          arrow
        >
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "flex", alignItems: "center", cursor: "default" }}
          >
            איך בוצע החישוב
            <Box component="span" mr={0.5}>
              <HelpOutlineIcon fontSize="small" color="disabled" />
            </Box>
          </Typography>
        </Tooltip>}
      </Box>
    </Box>
  );
};

export default WeightedProductYieldsSummary;
